import {
  Box,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect } from 'react';
import {
  Field,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';
import { useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';

const MAX_LENGTH = 255;
const NAME_FIELD = 'name';

interface RenameModalOwnProps {
  messengerId: string | null;
  show: boolean;
  closeModal: () => void;
  refreshItems: () => void;
}

interface RenameFormValues {
  name: string;
}

interface Errors {
  [NAME_FIELD]?: string;
}

type Props = RenameModalOwnProps &
  InjectedFormProps<RenameFormValues, RenameModalOwnProps, FormErrors<Errors>>;

function RenameModalImpl(props: Props): JSX.Element {
  const notify = useNotifications();
  const messengerApi = useDixaMessengerApi();
  const {
    messengerId,
    show,
    closeModal,
    refreshItems,
    handleSubmit,
    destroy: destroyForm,
  } = props;

  const handleFormSubmit = useCallback(
    async (values: RenameFormValues) => {
      const { name } = values;
      ampli.messengerFormSubmitted({ mode: 'rename' });

      try {
        if (!messengerId) return;

        await messengerApi.patch(messengerId, { name });

        notify({
          title: 'Messenger updated',
          text: `Messenger was renamed to "${name}".`,
          icon: 'done',
          className: 'success',
          time: 5000,
        });

        closeModal();
        refreshItems();
      } catch {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to update the messenger.`,
          icon: 'exclamation',
          className: 'danger',
        });
      }
    },
    [notify, closeModal, refreshItems, messengerId, messengerApi],
  );

  useEffect(() => {
    if (!show) {
      destroyForm();
    }
  }, [destroyForm, show]);

  return (
    <Modal isOpen={show} onClose={closeModal}>
      <Box width="600px">
        <ModalHead title="Rename messenger" onClose={closeModal} />
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <Field
              label="Name"
              name="name"
              maxLength={MAX_LENGTH}
              component={FieldText}
              type="text"
              required
              validate={validateName}
            />
          </ModalBody>
          <ModalFoot
            cancelOnClick={closeModal}
            confirmName="Update"
            confirmOnClick={handleSubmit(handleFormSubmit)}
            confirmProps={{ type: 'submit' }}
          />
        </Form>
      </Box>
    </Modal>
  );
}

export const RenameModal = reduxForm<
  RenameFormValues,
  RenameModalOwnProps,
  FormErrors
>({
  enableReinitialize: true,
  form: 'renameMessengerForm',
  touchOnBlur: false,
})(RenameModalImpl);
